import { useCallback } from "react"
import { useAuthBearerFetch, bearerFetcher } from "matsuri-hooks"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import { m2mCleaning_cleanerV4 } from "@/endpoints/m2m-cleaning.cleaner-v4"

export interface RoomSupplyItem {
    id: string
    code: string
    name: string
    shortName?: string
    category: string
}

// 物件が備品チェック対象外なら isEnabled=false ＋ items は空。
interface RoomSupplyItemListResponse {
    isEnabled: boolean
    items: RoomSupplyItem[]
}

// code / name はサーバーが品目マスタから解決する。マスタに無ければ返らない。
export interface RoomSupplyReportItem {
    itemId: string
    quantity: number
    code?: string
    name?: string
    shortName?: string
}

export interface RoomSupplyReportResponse {
    cleaningId: string
    listingId: string
    reportedBy: string
    // 報告日時（UNIX 秒）
    reportedAt: number
    items: RoomSupplyReportItem[]
}

/**
 * 備品の不足報告画面のデータ取得（対象判定・品目リスト・既存報告）と報告送信をまとめたフック。
 * `isEnabled` は取得前は undefined。`error` のときは対象かどうか判断できないため報告はさせず、
 * 画面側では品目リストの代わりにスキップ導線を出してフローは継続できるようにしている。
 *
 * `existingReport` は既に報告済みなら報告内容、未報告なら null。フローを戻って再度この画面を
 * 通っても二重報告させないため、画面側で報告済みなら送信させず完了表示にする。
 */
export const useRoomSupply = (cleaningId: string) => {
    const { token } = useAuth()

    const { data, isLoading, error } =
        useAuthBearerFetch<RoomSupplyItemListResponse>(
            token,
            cleaningId
                ? m2mCleaning_cleanerV4.getRoomSupplyItems({ cleaningId })
                : null,
            {
                method: m2mCleaning_cleanerV4.getRoomSupplyItems.method
            }
        )

    // 既存報告の取得。報告済みなら報告内容、未報告なら null が返る。
    const {
        data: existingReport,
        isLoading: isReportLoading,
        error: reportFetchError
    } = useAuthBearerFetch<RoomSupplyReportResponse | null>(
        token,
        cleaningId
            ? m2mCleaning_cleanerV4.getRoomSupplyReport({ cleaningId })
            : null,
        {
            method: m2mCleaning_cleanerV4.getRoomSupplyReport.method
        }
    )

    const completeReport = useCallback(
        async (items: { itemId: string; quantity: number }[]) => {
            const { data: report, error: reportError } =
                await bearerFetcher<RoomSupplyReportResponse>(
                    token ?? "",
                    m2mCleaning_cleanerV4.completeRoomSupplyReport({
                        cleaningId
                    }),
                    {
                        method: "POST",
                        body: JSON.stringify({ items })
                    }
                )
            if (reportError) {
                throw reportError
            }
            if (!report) {
                throw new Error("empty response")
            }
            return report
        },
        [token, cleaningId]
    )

    return {
        isEnabled: data?.isEnabled,
        items: data?.items ?? [],
        // 報告済みなら報告内容、取得済みで未報告なら null、取得前は undefined
        existingReport,
        isLoading: isLoading || isReportLoading,
        // 品目リスト取得のエラーのみ致命的に扱う（既存報告取得の失敗は握りつぶし、
        // 報告フロー自体は継続できるようにする）
        error,
        reportFetchError,
        completeReport
    }
}
